import { convertHTMLStringToDOM } from '@/utils/convertHTMLStringToDOM'
import useTranslation from 'next-translate/useTranslation'
import { FC, useState, useRef, useEffect } from 'react'
import { BorderedButton } from '@/components/atoms/button'
import { Box } from '@/components/atoms/Grid'
import { Small, Small2Base } from '@/components/atoms/typography'
import styled, { css } from 'styled-components'
import { SeoCategoryArticle, SeoCategoryFAQ } from '@/utils/seoData'
import { validFor } from '@/theme/validFor'

type ProductListingDescriptionProps = {
  description: string
  enableReadMore?: boolean
  maxHeight?: number
  title?: string
}

const ProductListingDescription: FC<ProductListingDescriptionProps> = ({
  description,
  enableReadMore = false,
  maxHeight = 450,
  title
}) => {
  if ((description?.length ?? 0) === 0) {
    return null
  }

  const { t } = useTranslation('common')
  const [expanded, setExpanded] = useState<boolean>(false)
  const [isOverflowing, setIsOverflowing] = useState<boolean>(false)
  const [contentHeight, setContentHeight] = useState<number>(0)
  const contentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const checkOverflow = () => {
      if (contentRef.current) {
        const scrollHeight = contentRef.current.scrollHeight
        setContentHeight(scrollHeight)
        setIsOverflowing(scrollHeight > maxHeight)
      }
    }

    checkOverflow()
    window.addEventListener('resize', checkOverflow)
    return () => window.removeEventListener('resize', checkOverflow)
  }, [description, maxHeight])

  const shouldFade = enableReadMore && !expanded && isOverflowing

  const seoTitle =
    title ||
    description.match(/<h1>(.*?)<\/h1>/)?.[1] ||
    t('Category.description')

  return (
    <Box textAlign="center">
      <SeoCategoryArticle title={seoTitle} description={description} />
      <SeoCategoryFAQ htmlContent={description} />

      <ContentWrapper
        ref={contentRef}
        $expanded={expanded}
        $maxHeight={maxHeight}
        $contentHeight={contentHeight}
        $shouldFade={shouldFade}
      >
        <StyledSmall className="fc-article" $isLegacyDescription={!shouldFade}>
          <div className="inner">{convertHTMLStringToDOM(description)}</div>
        </StyledSmall>
      </ContentWrapper>

      {enableReadMore && isOverflowing && (
        <ExpandButton
          mb={14}
          title={
            expanded ? t('Manufacturer.readLess') : t('Manufacturer.readMore')
          }
          onClick={() => setExpanded(!expanded)}
        />
      )}
    </Box>
  )
}

export default ProductListingDescription

const ContentWrapper = styled.div<{
  $expanded: boolean
  $maxHeight: number
  $contentHeight: number
  $shouldFade: boolean
}>`
  position: relative;
  overflow: hidden;

  max-height: ${({ $expanded, $maxHeight, $contentHeight }) =>
    $expanded ? `${$contentHeight}px` : `${$maxHeight}px`};
  transition: max-height 0.4s ease-in-out;

  ${({ $shouldFade }) =>
    $shouldFade
      ? css`
          -webkit-mask-image: linear-gradient(
            to bottom,
            black 50%,
            transparent 100%
          );
          mask-image: linear-gradient(to bottom, black 50%, transparent 100%);
        `
      : css`
          -webkit-mask-image: none;
          mask-image: none;
        `}
`

const ExpandButton = styled(BorderedButton)`
  align-items: center;
  text-transform: uppercase;
  padding: 0 25px;
  height: 38px;
  margin-top: 10px;

  & ${Small} {
    font-size: 12px;
    font-weight: 500;
  }
`

const StyledSmall = styled.div<{ $isLegacyDescription?: boolean }>`
  ${Small2Base};
  margin-bottom: 14px;
  margin-top: ${({ $isLegacyDescription }) =>
    $isLegacyDescription ? '28px' : '0'};
  text-align: left;
  ${validFor.mobile`
   margin-top: ${({ $isLegacyDescription }) =>
     $isLegacyDescription ? '40px' : '20px'};
    margin-bottom: 0;
  `}
  & a {
    color: inherit;
    text-decoration: underline;
  }

  & a:hover {
    text-decoration: none;
  }
`
